import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  MessageState,
  TextContentPartProps,
  useMessage,
} from '@assistant-ui/react'
import { unstable_memoizeMarkdownComponents as memoizeMarkdownComponents } from '@assistant-ui/react-markdown'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { format } from 'date-fns'
import { defaultSchema } from 'hast-util-sanitize'
import { marked } from 'marked'
import {
  HiMiniArrowDownTray,
  HiMiniChevronLeft,
  HiMiniChevronRight,
  HiOutlineGlobeAlt,
  HiOutlineSquare2Stack,
  HiSquare3Stack3D,
} from 'react-icons/hi2'
import { LuArrowUpRight, LuCheck, LuCopy, LuMaximize2 } from 'react-icons/lu'
import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import remarkGfm from 'remark-gfm'
import { toast } from 'sonner'

import {
  useChatAgentName,
  useChatEnvironmentStore,
  useChatOrgId,
  useChatPursuitId,
  useChatSessionId,
  useSetExpandedMarkdownTableKey,
} from '@/components/chat-v2/hooks/useChatEnvironment'
import {
  useOpportunityChatSession,
  usePursuitChatSession,
  usePursuitDocumentChatSession,
} from '@/components/chat-v2/hooks/useChatSession'
import {
  useLiveAssistantPartText,
  useReadLiveAssistantPartText,
} from '@/components/chat-v2/hooks/useLiveAssistantPartText'
import {
  useOpportunitySessionSource,
  useOrganizationSessionSource,
  usePursuitDocumentSessionSource,
  usePursuitSessionSource,
} from '@/components/chat-v2/hooks/useSource'
import {
  useOpportunitySourceFiles,
  usePursuitSourceFiles,
} from '@/components/chat-v2/hooks/useSourceFiles'
import { downloadTextFile, rowsToCsv } from '@/components/chat-v2/lib/export'
import { useChatCompactionStatus } from '@/components/chat-v2/providers/ChatCompactionStatusContext'
import type { ChatEnvironmentStoreState } from '@/components/chat-v2/providers/ChatEnvironmentProvider'
import { Favicon } from '@/components/chat-v2/ui/Favicon'
import {
  closeUnfinishedSyntax,
  isListToken,
  revealedListItemsPrefixLength,
} from '@/components/chat-v2/ui/MarkdownText/helper'
import {
  remarkNumberedCitations,
  remarkTableCitations,
  remarkWebCitations,
  remarkWebLinks,
} from '@/components/chat-v2/ui/MarkdownText/lib'
import { useSourceClick } from '@/components/chat-v2/ui/MarkdownText/SourceCitationRenderer'
import { getExpandedMarkdownTableKey } from '@/components/chat-v2/ui/MarkdownText/tableKey'
import type {
  SourceCitationHtmlProps,
  WebLinkProps,
  WebSearchCitationProps,
} from '@/components/chat-v2/ui/MarkdownText/types'
import { cleanPreviewMarkdown } from '@/components/chat-v2/ui/Tool/WebSearch/snippet'
import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import Button from '@/components/common/Button'
import Checkbox from '@/components/common/Checkbox'
import { Dialog, DialogContent, DialogTitle } from '@/components/common/Dialog'
import IconButton from '@/components/common/IconButtonV2'
import Spinner from '@/components/common/Spinner'
import { SweetspotMarkAnimated } from '@/components/common/SweetspotMarkAnimated'
import { RenderedTextArea } from '@/components/common/TextArea/RenderedTextArea'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import Markdown from '@/components/markdown/Markdown'

import { cleanFileName } from '@/helpers/file'
import { cn } from '@/helpers/shadcn/utils'
import { getFaviconUrl, isHttpUrl } from '@/helpers/url'

import { useOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'

import type { PaginatedResponse } from '@/types/Api'
import type {
  OpportunityChatSession,
  PursuitChatSession,
  PursuitDocumentChatSession,
  Source,
} from '@/types/chat-v2'
import type { MarkdownNodeProps } from '@/types/components/Markdown'
import type { PotentiallyUnhydratedFile, UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

// region: Types

interface SourceCitationProps {
  sourceId: number
}

// endregion: Types

interface FirstCitationNameProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  sourceId: number
  remainingCount: number
}

// Helper function to get display name from source and file
const getCitationDisplayName = (
  source: Source | null | undefined,
  file: PotentiallyUnhydratedFile | null | undefined,
): string | null => {
  if (!source) return null

  if (source.source_type === 'file') {
    if (file) {
      return cleanFileName(file.name)
    }

    if (source.file_name) {
      return cleanFileName(source.file_name)
    }

    return cleanFileName('Document')
  }

  if (source.source_type === 'web') {
    return source.url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
  }

  if (source.source_type === 'tool') {
    return source.tool_display_name ?? source.tool_name
  }

  if (source.source_type === 'pursuit') {
    return source.pursuit_title
  }

  return null
}

// Pill leading icon, rendered bare so it sits quietly in the pill: web
// citations get the site's favicon (lazy-loaded, URL cached per hostname +
// size bucket in helpers/url), file citations get the same file-type
// thumbnail used in the citation lists. Other source types keep the
// text-only pill.
const getCitationIcon = (
  source: Source | null | undefined,
  file: PotentiallyUnhydratedFile | null | undefined,
): React.ReactNode => {
  if (source?.source_type === 'web') {
    return (
      <Favicon
        url={getFaviconUrl(source.url, 16)}
        className="h-3 w-3"
        fallback={
          <HiOutlineGlobeAlt className="h-3 w-3 text-primary-400 dark:text-primary-500" />
        }
      />
    )
  }
  if (source?.source_type === 'file') {
    return (
      <CompactThumbnail
        name={file?.name ?? source.file_name ?? 'Document'}
        size={12}
      />
    )
  }
  return undefined
}

// region: Citation data

// One data hook per agent, shared by the pill trigger and its popover card so
// the pair can't drift apart. Pills are always mounted and a long thread
// holds hundreds, so the rules here keep cache traffic from fanning out into
// thread-wide re-renders: every session subscription goes through a narrow
// `select` (subscribing to the whole session object re-renders every pill on
// any session cache write), and file lookups select a filtered slice whose
// reference React Query's structural sharing keeps stable across unrelated
// cache updates.

interface CitationData {
  source: Source | undefined
  file: PotentiallyUnhydratedFile | null
  isLoading: boolean
}

const selectOpportunityScope = (session: OpportunityChatSession) => ({
  opportunityId: session.opportunity_id,
  opportunityType: session.opportunity_type,
})

const selectPursuitId = (session: PursuitChatSession) => session.pursuit_id

const selectPursuitDocumentId = (session: PursuitDocumentChatSession) =>
  session.pursuit_id

const useOpportunityCitationData = (sourceId: number): CitationData => {
  const orgId = useChatOrgId()
  const sessionId = useChatSessionId()

  const { data: source, isLoading: isSourceLoading } =
    useOpportunitySessionSource({ sourceId })
  const { data: scope, isLoading: isScopeLoading } = useOpportunityChatSession({
    orgId,
    sessionId: sessionId ?? '',
    select: selectOpportunityScope,
  })

  const selectFile = useCallback(
    (files: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      if (source?.source_type !== 'file') return []
      return files.data.filter((file) => file.id === source.file_id)
    },
    [source],
  )
  const { data: files, isLoading: isFileLoading } = useOpportunitySourceFiles({
    orgId,
    opportunityId: scope?.opportunityId ?? ('' as UUID),
    opportunityType: scope?.opportunityType ?? 'federal_contract',
    select: selectFile,
  })

  return {
    source,
    file: files?.[0] ?? null,
    isLoading: isSourceLoading || isScopeLoading || isFileLoading,
  }
}

const useOrganizationCitationData = (sourceId: number): CitationData => {
  const orgId = useChatOrgId()

  const { data: source, isLoading: isSourceLoading } =
    useOrganizationSessionSource({ sourceId })

  const selectFile = useCallback(
    (files: PaginatedResponse<UnhydratedFile>) => {
      if (source?.source_type !== 'file') return undefined
      return files.data.find((file) => file.id === source.file_id)
    },
    [source],
  )
  const { data: file, isLoading: isFileLoading } = useOrganizationFiles({
    orgId,
    selector: selectFile,
  })

  return {
    source,
    file: file ?? null,
    isLoading: isSourceLoading || isFileLoading,
  }
}

const usePursuitCitationData = (sourceId: number): CitationData => {
  const orgId = useChatOrgId()
  const sessionId = useChatSessionId()

  const { data: source, isLoading: isSourceLoading } = usePursuitSessionSource({
    sourceId,
  })
  const { data: pursuitId, isLoading: isSessionLoading } =
    usePursuitChatSession({
      orgId,
      sessionId: sessionId ?? '',
      select: selectPursuitId,
    })

  const selectFile = useCallback(
    (files: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      if (source?.source_type !== 'file') return []
      return files.data.filter((file) => file.id === source.file_id)
    },
    [source],
  )
  const { data: files, isLoading: isFileLoading } = usePursuitSourceFiles({
    orgId,
    pursuitId: pursuitId ?? ('' as UUID),
    select: selectFile,
  })

  return {
    source,
    file: files?.[0] ?? null,
    isLoading: isSourceLoading || isSessionLoading || isFileLoading,
  }
}

const usePursuitDocumentCitationData = (sourceId: number): CitationData => {
  const orgId = useChatOrgId()
  const sessionId = useChatSessionId()

  const { data: source, isLoading: isSourceLoading } =
    usePursuitDocumentSessionSource({ sourceId })
  const { data: pursuitId, isLoading: isSessionLoading } =
    usePursuitDocumentChatSession({
      orgId,
      sessionId: sessionId ?? '',
      select: selectPursuitDocumentId,
    })

  const selectFile = useCallback(
    (files: PaginatedResponse<PotentiallyUnhydratedFile>) => {
      if (source?.source_type !== 'file') return []
      return files.data.filter((file) => file.id === source.file_id)
    },
    [source],
  )
  const { data: files, isLoading: isFileLoading } = usePursuitSourceFiles({
    orgId,
    pursuitId: pursuitId ?? ('' as UUID),
    select: selectFile,
  })

  return {
    source,
    file: files?.[0] ?? null,
    isLoading: isSourceLoading || isSessionLoading || isFileLoading,
  }
}

// endregion: Citation data

// Helper function to get agent citation component based on agent name
const getAgentCitationComponent = (
  agentName: string | null,
  pursuitId: UUID | null | undefined,
) => {
  switch (agentName) {
    case 'opportunity_chat':
      return OpportunityAgentSourceCitation
    case 'organization_chat':
      return OrganizationAgentSourceCitation
    case 'pursuit_chat':
      return PursuitAgentSourceCitation
    case 'pursuit_document_chat':
      return PursuitDocumentAgentSourceCitation
    default:
      return null
  }
}

const NoopButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      style={{ display: 'none' }}
      aria-hidden="true"
      {...props}
    />
  )
})

NoopButton.displayName = 'ChatV2.Ui.MarkdownText.NoopButton'

interface CitationTriggerWrapperProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  /** Small leading visual (web favicon, file-type thumbnail). */
  icon?: React.ReactNode
  idRange?: string
  remainingCount?: number
}

export const CitationTriggerWrapper = forwardRef<
  HTMLButtonElement,
  CitationTriggerWrapperProps
>(({ children, icon, idRange, remainingCount, className, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex items-center justify-center font-medium gap-1',
        'h-[18px] py-[1px] rounded-full text-[11px]',
        icon != null ? 'pl-[4px] pr-[6px]' : 'px-[5px]',
        'bg-primary-100 dark:bg-primary-800/70',
        // Only cheap properties transition; animating box-shadow or filter
        // forces repaints across hundreds of always-mounted pills.
        'transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.2,0,0,1)]',
        'ring-1 ring-primary-200/80 dark:ring-primary-700/60',
        'hover:bg-primary-200/70 dark:hover:bg-primary-700/70',
        'text-primary-600 america:text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-100',
        'active:scale-[0.96]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/45 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-primary-950',
        // Pills are navigation chrome, not prose: keep them out of text
        // selection so copied text reads clean. No transform-gpu: promoting
        // every pill to its own compositor layer makes long threads janky.
        'cursor-pointer select-none',
        'group',
        className,
      )}
      {...props}
    >
      {icon != null && (
        <span className="flex h-3 w-3 flex-shrink-0 items-center justify-center overflow-hidden rounded-[3px]">
          {icon}
        </span>
      )}
      <span className="truncate max-w-[84px]">{children}</span>
      {idRange && (
        <span className="flex-shrink-0 text-primary-500 dark:text-primary-400">
          {idRange}
        </span>
      )}
      {!idRange && remainingCount != null && remainingCount > 0 && (
        <span className="flex-shrink-0">+{remainingCount}</span>
      )}
    </button>
  )
})

CitationTriggerWrapper.displayName =
  'ChatV2.Ui.MarkdownText.CitationTriggerWrapper'

// The pill trigger for each agent differs only in where its data comes from;
// each is stamped out from the matching shared data hook. Memoized because
// pills live inside always-mounted markdown and must not re-render with
// parent parse churn.
const createFirstCitationName = (
  useCitationData: (sourceId: number) => CitationData,
  displayName: string,
) => {
  const FirstCitationName = memo(
    forwardRef<HTMLButtonElement, FirstCitationNameProps>(
      ({ sourceId, remainingCount, ...props }, ref) => {
        const { onClick, ...triggerProps } = props
        const { source, file } = useCitationData(sourceId)
        const name = getCitationDisplayName(source, file)
        const onSourceClick = useSourceClick(source, file)

        const handleClick = useCallback(
          (event: React.MouseEvent<HTMLButtonElement>) => {
            onClick?.(event)
            onSourceClick(sourceId)
          },
          [onClick, onSourceClick, sourceId],
        )

        if (!name) {
          return <NoopButton ref={ref} {...triggerProps} />
        }

        return (
          <CitationTriggerWrapper
            ref={ref}
            {...triggerProps}
            onClick={handleClick}
            icon={getCitationIcon(source, file)}
            remainingCount={remainingCount}
          >
            {name}
          </CitationTriggerWrapper>
        )
      },
    ),
  )
  FirstCitationName.displayName = displayName
  return FirstCitationName
}

const OpportunityFirstCitationName = createFirstCitationName(
  useOpportunityCitationData,
  'ChatV2.Ui.MarkdownText.OpportunityFirstCitationName',
)

const OrganizationFirstCitationName = createFirstCitationName(
  useOrganizationCitationData,
  'ChatV2.Ui.MarkdownText.OrganizationFirstCitationName',
)

const PursuitFirstCitationName = createFirstCitationName(
  usePursuitCitationData,
  'ChatV2.Ui.MarkdownText.PursuitFirstCitationName',
)

const PursuitDocumentFirstCitationName = createFirstCitationName(
  usePursuitDocumentCitationData,
  'ChatV2.Ui.MarkdownText.PursuitDocumentFirstCitationName',
)

// Wrapper component that conditionally renders the correct citation component
const ConditionalFirstCitationName = forwardRef<
  HTMLButtonElement,
  FirstCitationNameProps & {
    agentName: string | null
    pursuitId: UUID | null | undefined
  }
>(({ agentName, pursuitId, ...props }, ref) => {
  switch (agentName) {
    case 'opportunity_chat':
      return <OpportunityFirstCitationName ref={ref} {...props} />
    case 'organization_chat':
      return <OrganizationFirstCitationName ref={ref} {...props} />
    case 'pursuit_chat':
      return <PursuitFirstCitationName ref={ref} {...props} />
    case 'pursuit_document_chat':
      return <PursuitDocumentFirstCitationName ref={ref} {...props} />
    default:
      return <NoopButton ref={ref} {...props} />
  }
})

ConditionalFirstCitationName.displayName =
  'ChatV2.Ui.MarkdownText.ConditionalFirstCitationName'

interface BaseCitationItemProps {
  source: Source
  file: PotentiallyUnhydratedFile | undefined | null
  sourceId?: number
  children: React.ReactNode
  onClick: () => void
  labelOverride?: string
}

export const BaseCitationItem: React.FC<BaseCitationItemProps> = ({
  source,
  file,
  sourceId,
  children,
  onClick,
  labelOverride,
}) => {
  const faviconUrl =
    source.source_type === 'web' ? getFaviconUrl(source.url, 16) : null

  const name = getCitationDisplayName(source, file)

  if (!name) {
    return null
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full group/item flex flex-col gap-0.5 items-start',
        'px-2 py-1.5 rounded-md text-left',
        'hover:bg-primary-100 dark:hover:bg-primary-800',
        'transition-colors duration-100',
        'cursor-pointer',
        'outline-none focus:outline-none',
      )}
    >
      <div className="flex items-center justify-between gap-1.5 w-full">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <div className="text-primary-600 dark:text-primary-400 group-hover/item:text-primary-900 dark:group-hover/item:text-primary-100 transition-colors flex-shrink-0">
            {source.source_type === 'file' ? (
              <CompactThumbnail
                name={file?.name ?? name ?? 'Document'}
                size={12}
              />
            ) : source.source_type === 'web' ? (
              <Favicon
                url={faviconUrl}
                className="w-3 h-3 rounded-[1px]"
                fallback={<HiOutlineGlobeAlt className="w-3 h-3" />}
              />
            ) : source.source_type === 'pursuit' ? (
              <HiSquare3Stack3D className="w-3 h-3" />
            ) : (
              <HiOutlineSquare2Stack className="w-3 h-3" />
            )}
          </div>
          <span className="text-sm text-primary-600 dark:text-primary-300 group-hover/item:text-primary-900 dark:group-hover/item:text-primary-100 transition-colors truncate">
            {source.source_type === 'file'
              ? name
              : source.source_type === 'web'
                ? source.url
                    .replace(/^https?:\/\//, '')
                    .replace(/^www\./, '')
                    .split('/')[0]
                : source.source_type === 'tool'
                  ? (source.tool_display_name ?? source.tool_name)
                  : source.source_type === 'pursuit'
                    ? source.pursuit_title
                    : 'Unknown'}
          </span>
        </div>
        {(labelOverride || sourceId !== undefined) && (
          <span className="text-[13px] text-primary-500 dark:text-primary-500 flex-shrink-0">
            {labelOverride ?? `[${sourceId}]`}
          </span>
        )}
      </div>
      {children}
    </button>
  )
}

BaseCitationItem.displayName = 'MarkdownText.BaseCitationItem'

interface CitationItemProps {
  source: Source | undefined | null
  file: PotentiallyUnhydratedFile | undefined | null
  sourceId: number
  isLoading?: boolean
}

// The source-type icon shown at the top of a citation detail card.
const citationCardIcon = (
  source: Source,
  file: PotentiallyUnhydratedFile | undefined | null,
  name: string,
): React.ReactNode => {
  if (source.source_type === 'web') {
    return (
      <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-black/10 dark:ring-white/20">
        <Favicon
          url={getFaviconUrl(source.url, 16)}
          className="h-3 w-3"
          fallback={<HiOutlineGlobeAlt className="h-3 w-3 text-primary-500" />}
        />
      </span>
    )
  }
  if (source.source_type === 'file') {
    return <CompactThumbnail name={file?.name ?? name} size={14} />
  }
  if (source.source_type === 'pursuit') {
    return <HiSquare3Stack3D className="h-3.5 w-3.5 text-primary-500" />
  }
  return <HiOutlineSquare2Stack className="h-3.5 w-3.5 text-primary-500" />
}

const citationFileMetadata = (source: Source): string[] => {
  if (source.source_type !== 'file') return []
  const metadata: string[] = []
  if (source.is_page_image) {
    metadata.push('Image')
  } else if (source.file_page !== undefined) {
    metadata.push(`Page ${source.file_page}`)
  }
  if (source.line_range && source.line_range[0] !== source.line_range[1]) {
    metadata.push(`Lines ${source.line_range[0]}-${source.line_range[1]}`)
  } else if (source.line_range) {
    metadata.push(`Line ${source.line_range[0]}`)
  }
  return metadata
}

// Muted markdown for the citation-card preview: file excerpts carry real
// markdown (headings, lists, tables), but a four-line hover card must not
// shout — every block renders as plain xs prose at the card's own size.
// Tables are the exception: pipe rows as running text are unreadable, so
// they keep a compact grid. Links flatten to text (the card is a button)
// and images/rules are dropped outright.
const citationPreviewMarkdownComponents: Components = {
  p: ({ children }) => <p className="my-0">{children}</p>,
  h1: ({ children }) => <p className="my-0 font-medium">{children}</p>,
  h2: ({ children }) => <p className="my-0 font-medium">{children}</p>,
  h3: ({ children }) => <p className="my-0 font-medium">{children}</p>,
  h4: ({ children }) => <p className="my-0 font-medium">{children}</p>,
  h5: ({ children }) => <p className="my-0 font-medium">{children}</p>,
  h6: ({ children }) => <p className="my-0 font-medium">{children}</p>,
  a: ({ children }) => <span>{children}</span>,
  img: () => null,
  hr: () => null,
  blockquote: ({ children }) => <div className="my-0">{children}</div>,
  pre: ({ children }) => (
    <div className="my-0 whitespace-pre-wrap">{children}</div>
  ),
  code: ({ children }) => (
    <code className="font-mono text-[11px]">{children}</code>
  ),
  ul: ({ children }) => <ul className="my-0 list-disc pl-4">{children}</ul>,
  ol: ({ children }) => <ol className="my-0 list-decimal pl-4">{children}</ol>,
  li: ({ children }) => <li className="my-0">{children}</li>,
  table: ({ children }) => (
    <table className="my-1 w-full border-collapse text-[11px] leading-4">
      {children}
    </table>
  ),
  th: ({ children }) => (
    <th className="border border-primary-200 px-1.5 py-0.5 text-left font-medium dark:border-primary-700">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-primary-200 px-1.5 py-0.5 align-top dark:border-primary-700">
      {children}
    </td>
  ),
}

const citationPreviewRemarkPlugins = [remarkGfm]

// A line starting and ending in pipes is a markdown table row.
const CITATION_PREVIEW_TABLE_RE = /^\s*\|.*\|\s*$/m

/**
 * Detail card shown in the citation hover popover: source identity up top,
 * then the cited page title and the stored highlight so the reader can judge
 * the citation without leaving the message. Clicking opens the source viewer.
 * Memoized: the carousel keeps every card in a group mounted, so a page flip
 * must not re-render (and re-clean) the neighbors.
 */
const CitationItem = memo(function CitationItem({
  source,
  file,
  sourceId,
  isLoading = false,
}: CitationItemProps) {
  const handleClick = useSourceClick(source, file)

  const handleButtonClick = useCallback(() => {
    handleClick(sourceId)
  }, [handleClick, sourceId])

  // HTML previews (file excerpts) are already-bounded stored highlights and
  // must render as markup, matching CitationList. Text previews can be a
  // whole fetched article; the card clamps to four lines, so slice before
  // cleaning instead of paying regex passes (and DOM weight) for the full
  // text.
  const preview = useMemo(() => {
    const raw = source?.content_preview
    if (!raw) return null
    if (source.content_preview_type === 'html') return raw
    return cleanPreviewMarkdown(raw.slice(0, 600))
  }, [source?.content_preview, source?.content_preview_type])
  const previewHasTable =
    preview != null &&
    source?.content_preview_type !== 'html' &&
    CITATION_PREVIEW_TABLE_RE.test(preview)

  // Loading state (min height keeps the popover from jumping while a
  // carousel neighbor loads in).
  if (isLoading || !source) {
    return (
      <div className="flex min-h-[68px] w-full items-center justify-center px-2 py-1.5">
        <Spinner className="w-3 h-3" />
      </div>
    )
  }

  const name = getCitationDisplayName(source, file)
  if (!name) return null

  const title = source.source_type === 'web' ? source.title : null
  const metadata = citationFileMetadata(source)

  return (
    <button
      onClick={handleButtonClick}
      className={cn(
        'w-full flex flex-col items-start gap-1 rounded-md px-2.5 py-2 text-left',
        'hover:bg-primary-100 dark:hover:bg-primary-800',
        'transition-colors duration-100 cursor-pointer',
        'outline-none focus:outline-none',
      )}
    >
      <div className="flex w-full items-center gap-1.5">
        {citationCardIcon(source, file, name)}
        <span className="min-w-0 truncate text-xs font-medium text-primary-500 dark:text-primary-400">
          {name}
        </span>
      </div>
      {title && (
        <div className="text-sm font-medium text-primary-900 dark:text-primary-100 line-clamp-2">
          {title}
        </div>
      )}
      {metadata.length > 0 && (
        <div className="text-xs text-primary-500 dark:text-primary-400">
          {metadata.join(' • ')}
        </div>
      )}
      {preview &&
        (source.content_preview_type === 'html' ? (
          <RenderedTextArea
            components={citationPreviewMarkdownComponents}
            className="text-xs leading-5 text-primary-600 dark:text-primary-400 line-clamp-4 prose-p:my-0"
          >
            {preview}
          </RenderedTextArea>
        ) : (
          <div
            className={cn(
              'w-full min-w-0 text-xs leading-5 text-primary-600 dark:text-primary-400',
              // line-clamp (webkit-box) breaks table layout, so table
              // previews get a height cap instead of a line count.
              previewHasTable ? 'max-h-44 overflow-hidden' : 'line-clamp-4',
            )}
          >
            <Markdown
              remarkPlugins={citationPreviewRemarkPlugins}
              components={citationPreviewMarkdownComponents}
              logError={false}
            >
              {preview}
            </Markdown>
          </div>
        ))}
    </button>
  )
})

interface BaseSourceCitationProps {
  ids: number[]
  children: React.ReactNode
  trigger: React.ReactNode
}

export const BaseSourceCitation: React.FC<BaseSourceCitationProps> = ({
  ids,
  trigger,
  children,
}) => {
  if (ids.length === 0) return null

  return (
    <span className="mx-0.5 inline-block align-top">
      <TooltipPrimitive.Provider delayDuration={0}>
        <TooltipPrimitive.Root>
          <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>

          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              className={cn(
                'z-50 w-fit max-w-[400px] p-0 bg-white dark:bg-primary-900',
                'border border-primary-300/80 dark:border-primary-700/80 shadow-md',
                'rounded-md animate-in fade-in-0 zoom-in-95',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                'max-h-[368px] overflow-y-auto',
              )}
              side="bottom"
              sideOffset={4}
            >
              <div className="p-2 space-y-0.5">{children}</div>
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
      </TooltipPrimitive.Provider>
    </span>
  )
}

BaseSourceCitation.displayName = 'MarkdownText.BaseSourceCitation'

interface CitationCarouselProps {
  ids: number[]
  ItemComponent: React.ComponentType<SourceCitationProps>
}

/**
 * Pages the citation popover one detail card at a time (ChatGPT-style):
 * back/forward arrows plus an "n/N" position readout for multi-source
 * citations. Every card in the group stays mounted (groups are small), so
 * all sources resolve as soon as the popover opens and paging is an instant
 * visibility toggle instead of a remount + refetch per arrow click.
 */
const CitationCarousel: React.FC<CitationCarouselProps> = ({
  ids,
  ItemComponent,
}) => {
  const [index, setIndex] = useState(0)
  // ids can shrink between renders (streaming edits); clamp instead of
  // syncing state so the card never points past the end.
  const current = Math.min(index, ids.length - 1)

  const goPrev = useCallback(() => {
    setIndex((prev) => Math.max(Math.min(prev, ids.length - 1) - 1, 0))
  }, [ids.length])

  const goNext = useCallback(() => {
    setIndex((prev) => Math.min(prev + 1, ids.length - 1))
  }, [ids.length])

  return (
    <div className="w-72">
      {ids.length > 1 && (
        <div className="mb-1 flex items-center justify-between border-b border-primary-200 px-1 pb-1 dark:border-primary-800">
          <div className="flex items-center gap-0.5">
            <IconButton
              aria-label="Previous source"
              onClick={goPrev}
              disabled={current === 0}
              className="p-0.5"
            >
              <HiMiniChevronLeft className="h-3.5 w-3.5" aria-hidden />
            </IconButton>
            <IconButton
              aria-label="Next source"
              onClick={goNext}
              disabled={current === ids.length - 1}
              className="p-0.5"
            >
              <HiMiniChevronRight className="h-3.5 w-3.5" aria-hidden />
            </IconButton>
          </div>
          <span className="text-xs tabular-nums text-primary-500 dark:text-primary-400">
            {current + 1}/{ids.length}
          </span>
        </div>
      )}
      {ids.map((id, i) => (
        <div key={id} className={i === current ? undefined : 'hidden'}>
          <ItemComponent sourceId={id} />
        </div>
      ))}
    </div>
  )
}

CitationCarousel.displayName = 'MarkdownText.CitationCarousel'

// The parse hands SourceCitation fresh `node`/`children` objects on every
// re-parse of its containing block, so default shallow comparison can never
// hold exactly where the memo matters most: a streaming active tail. The
// pill renders purely from its data attributes — compare only those, so
// tail re-parses bail here instead of re-rendering every pill's
// Tooltip/Popper/thumbnail stack on each token.
const sourceCitationPropsEqual = (
  prev: SourceCitationHtmlProps,
  next: SourceCitationHtmlProps,
): boolean =>
  prev['data-id'] === next['data-id'] && prev['data-ids'] === next['data-ids']

const SourceCitation = memo(function SourceCitation({
  'data-id': dataId,
  'data-ids': dataIds,
}: SourceCitationHtmlProps) {
  const ids = useMemo(() => {
    if (dataIds) {
      return dataIds
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id))
    }
    if (dataId) {
      const id = parseInt(dataId, 10)
      return isNaN(id) ? [] : [id]
    }
    return []
  }, [dataId, dataIds])

  const pursuitId = useChatPursuitId()
  const agentName = useChatAgentName()

  const AgentCitationComponent = getAgentCitationComponent(agentName, pursuitId)

  if (ids.length === 0) return null
  if (!AgentCitationComponent) return null

  const firstCitationId = ids[0]
  const remainingCount = ids.length - 1

  return (
    <BaseSourceCitation
      ids={ids}
      trigger={
        <ConditionalFirstCitationName
          agentName={agentName}
          pursuitId={pursuitId}
          sourceId={firstCitationId}
          remainingCount={remainingCount}
        />
      }
    >
      <CitationCarousel ids={ids} ItemComponent={AgentCitationComponent} />
    </BaseSourceCitation>
  )
}, sourceCitationPropsEqual)

SourceCitation.displayName = 'MarkdownText.SourceCitation'

const stopLinkPropagation = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.stopPropagation()
}

const WebSearchCitation = memo(function WebSearchCitation({
  'data-url': dataUrl,
  'data-text': dataText,
  children,
}: WebSearchCitationProps) {
  if (!isHttpUrl(dataUrl)) return <>{dataText || children}</>

  const faviconUrl = getFaviconUrl(dataUrl, 16)

  return (
    <span className="ml-[3px]">
      <a
        href={dataUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'inline-flex items-center justify-center font-medium gap-1',
          'min-w-[18px] h-[16px] px-[4px] py-[1px] rounded-full text-[11px]',
          'text-primary-700 dark:text-primary-200',
          'bg-primary-100 dark:bg-primary-800',
          'transition-colors duration-200',
          'shadow-sm dark:shadow-primary-900/20',
          'ring-1 ring-primary-300/80 dark:ring-primary-700/80',
          'mx-[2px]',
          'hover:bg-primary-200 dark:hover:bg-primary-700/70',
          'no-underline select-none',
        )}
      >
        <Favicon url={faviconUrl} className="w-2.5 h-2.5 flex-shrink-0" />
        <span className="line-clamp-1">{dataText || children}</span>
      </a>
    </span>
  )
})

WebSearchCitation.displayName = 'MarkdownText.WebSearchCitation'

const WebLink: React.FC<WebLinkProps> = ({ 'data-url': dataUrl, children }) => {
  if (!isHttpUrl(dataUrl)) return <>{children}</>

  return (
    <a
      href={dataUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'inline-flex items-center',
        'text-primary-700 dark:text-primary-300',
        'hover:text-primary-900 dark:hover:text-primary-100',
        'transition-colors duration-200',
        'underline decoration-dotted underline-offset-2',
      )}
    >
      <span>{children}</span>
      <LuArrowUpRight className="ml-1 w-3 h-3 flex-shrink-0" />
    </a>
  )
}

WebLink.displayName = 'MarkdownText.WebLink'

interface WebCitationProps {
  'data-id': string
  'data-url': string
}

const WebCitation = memo(function WebCitation({
  'data-id': dataId,
  'data-url': dataUrl,
}: WebCitationProps) {
  if (!isHttpUrl(dataUrl)) {
    return <SourceCitation data-id={dataId} />
  }

  const faviconUrl = getFaviconUrl(dataUrl, 16)
  const displayUrl = dataUrl
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]

  return (
    <span className="ml-[3px] inline-block align-middle">
      <a
        href={dataUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'inline-flex items-center justify-center font-medium gap-1.5',
          'h-5 px-2 py-0.5 rounded-full text-[11px]',
          'max-w-[200px]',
          'text-primary-700 dark:text-primary-200',
          'bg-primary-100 dark:bg-primary-800',
          'transition-colors duration-200',
          'shadow-sm dark:shadow-primary-900/20',
          'ring-1 ring-primary-300/80 dark:ring-primary-700/80',
          'mx-[2px]',
          'hover:bg-primary-200 dark:hover:bg-primary-700/70',
          'no-underline select-none',
        )}
        onClick={stopLinkPropagation}
      >
        <Favicon
          url={faviconUrl}
          className="w-3 h-3 flex-shrink-0 rounded-[1px]"
          fallback={
            <HiOutlineGlobeAlt className="w-3 h-3 text-primary-600 dark:text-primary-400" />
          }
        />
        <span className="truncate max-w-[120px]">{displayUrl}</span>
      </a>
    </span>
  )
})

WebCitation.displayName = 'MarkdownText.WebCitation'

// region: Default Markdown Components

type CustomComponents = Parameters<typeof memoizeMarkdownComponents>[0] & {
  'source-citation'?: React.ComponentType<SourceCitationHtmlProps>
  'web-search-citation'?: React.ComponentType<WebSearchCitationProps>
  'web-link'?: React.ComponentType<WebLinkProps>
  'web-citation'?: React.ComponentType<WebCitationProps>
}

const selectMessageIsStreaming = (state: MessageState) =>
  state.status?.type === 'running'
const selectMessageId = (state: MessageState) => state.id

const extractMarkdownTableRows = (
  tableElement: HTMLTableElement,
): string[][] => {
  return Array.from(tableElement.rows).map((row) =>
    Array.from(row.cells).map((cell) => cell.textContent ?? ''),
  )
}

const getMarkdownTableExportFilename = (messageId: string | null): string => {
  const messageSuffix = messageId?.slice(0, 8) ?? 'table'
  const dateSuffix = format(new Date(), 'yyyy-MM-dd')

  return `sweetspot-chat-table-${messageSuffix}-${dateSuffix}.csv`
}

const ExpandableMarkdownTable: React.FC<
  React.ComponentPropsWithoutRef<'table'> & MarkdownNodeProps
> = ({ className, children, node, ...props }) => {
  const isStreaming = useMessage(selectMessageIsStreaming)
  const messageId = useMessage(selectMessageId)
  const setExpandedTableKey = useSetExpandedMarkdownTableKey()
  const expandedTableRef = useRef<HTMLTableElement | null>(null)

  // Computed once at mount so streamed row updates do not close an open dialog.
  // The content-based signature still separates repeated same-header tables.
  const [tableKey] = useState(() =>
    getExpandedMarkdownTableKey({ messageId, children, node }),
  )

  const isExpanded = useChatEnvironmentStore(
    useCallback(
      (state: ChatEnvironmentStoreState) =>
        state.expandedMarkdownTableKey === tableKey,
      [tableKey],
    ),
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setExpandedTableKey((prev) => {
        if (open) return tableKey
        return prev === tableKey ? null : prev
      })
    },
    [setExpandedTableKey, tableKey],
  )

  const handleExpandClick = useCallback(() => {
    setExpandedTableKey(tableKey)
  }, [setExpandedTableKey, tableKey])

  const handleExportCsv = useCallback(() => {
    const tableElement = expandedTableRef.current

    if (!tableElement) {
      toast.error('Unable to export the table right now.')
      return
    }

    const rows = extractMarkdownTableRows(tableElement)

    if (rows.length === 0) {
      toast.error('No table data is available to export.')
      return
    }

    downloadTextFile(
      rowsToCsv(rows),
      getMarkdownTableExportFilename(messageId),
      'text/csv;charset=utf-8',
    )
    toast.success('Table exported to CSV!')
  }, [messageId])

  const inlineTableClassName = cn(
    'w-full min-w-max border-separate border-spacing-0 text-[13px] [&_th]:align-top [&_td]:align-top',
    className,
  )

  const expandedTableClassName = cn(
    inlineTableClassName,
    'min-w-[1400px]',
    '[&_tr:nth-child(even)_td]:bg-primary-50/40 dark:[&_tr:nth-child(even)_td]:bg-primary-900/20',
    '[&_th]:min-w-[220px] [&_td]:min-w-[220px]',
  )

  return (
    <>
      <div className="group/table my-5">
        <div className="sticky top-2 z-10 h-0 pointer-events-none">
          <div className="flex justify-end pr-2 pt-2">
            <div
              className={cn(
                'transition-all duration-200 ease-out',
                isStreaming
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 translate-y-1 pointer-events-none group-hover/table:opacity-100 group-hover/table:translate-y-0 group-hover/table:pointer-events-auto',
              )}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleExpandClick}
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-md',
                      'text-primary-600 hover:text-primary-900 dark:text-primary-300 dark:hover:text-primary-100',
                      'bg-primary-100/95 hover:bg-primary-200 dark:bg-primary-900/95 dark:hover:bg-primary-800',
                      'border border-primary-200 dark:border-primary-700 shadow-sm backdrop-blur-sm',
                    )}
                    aria-label="Expand table"
                  >
                    <LuMaximize2 size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" sideOffset={4}>
                  Expand table
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className={inlineTableClassName} {...props}>
            {children}
          </table>
        </div>
      </div>
      <Dialog open={isExpanded} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          closeButtonClassName="right-6 top-7 -translate-y-1/2"
          className="flex max-h-[92vh] w-[96vw] max-w-[96vw] flex-col overflow-hidden p-0"
        >
          <div className="flex min-h-14 items-center justify-between gap-3 border-b border-primary-200 px-6 pr-16 dark:border-primary-800">
            <DialogTitle className="text-base font-semibold text-primary-900 dark:text-primary-100">
              Expanded Table View
            </DialogTitle>
            <Button variant="outline-compact" onClick={handleExportCsv}>
              <HiMiniArrowDownTray size={16} className="mr-1" />
              Export CSV
            </Button>
          </div>
          <div className="flex-1 overflow-auto px-6 py-4">
            <table
              ref={expandedTableRef}
              className={expandedTableClassName}
              {...props}
            >
              {children}
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Outer margins trim at the first and last block of the whole answer. The
// streaming renderer splits its blocks across two `contents` wrappers
// (.md-part), and the edge a wrapper shares with its sibling is not an edge of
// the answer.
const EDGE_TRIM =
  '[&:first-child:not(.md-part:not(:first-child)>*)]:mt-0 [&:last-child:not(.md-part:not(:last-child)>*)]:mb-0'

// Standard markdown components wrapped with memoization to prevent
// re-renders during parent component updates (e.g., panel resize)
const memoizedMarkdownComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        'mb-8 mt-8 scroll-m-20 text-3xl font-medium tracking-tight',
        EDGE_TRIM,
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        'mb-4 mt-8 scroll-m-20 text-2xl font-medium tracking-tight',
        EDGE_TRIM,
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        'mb-4 mt-6 scroll-m-20 text-xl font-medium tracking-tight',
        EDGE_TRIM,
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        'mb-4 mt-6 scroll-m-20 text-lg font-medium tracking-tight',
        EDGE_TRIM,
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn('my-4 text-md font-medium', EDGE_TRIM, className)}
      style={{
        // TODO: why isn't text-md working?
        fontSize: '16px',
      }}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn('my-4 text-sm font-medium', EDGE_TRIM, className)}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn('text-sm mb-5 mt-5 leading-6', EDGE_TRIM, className)}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn(
        'text-blue-600 dark:text-blue-400 font-medium underline underline-offset-4 hover:opacity-70',
        className,
      )}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn('border-l-2 pl-6 italic', className)}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li className={cn('leading-5', className)} {...props} />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn('my-5 ml-6 list-disc [&>li]:mt-2', className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn('my-5 ml-6 list-decimal [&>li]:mt-2', className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn('my-5 border-b', className)} {...props} />
  ),
  table: ({ className, ...props }) => (
    <ExpandableMarkdownTable className={className} {...props} />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        'bg-primary-100 dark:bg-primary-900 border-l border-t border-b last:border-r border-primary-200 dark:border-primary-800 px-4 py-3 text-left font-bold whitespace-pre-wrap break-words [&[align=center]]:text-center [&[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        'border-b border-l px-4 py-2 text-left leading-5 whitespace-pre-wrap break-words last:border-r [&[align=center]]:text-center [&[align=right]]:text-right border-primary-200 dark:border-primary-800',
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn('m-0 border-b p-0 first:border-t', className)}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn('[&>a]:text-xs [&>a]:no-underline', className)}
      {...props}
    />
  ),
  strong: ({ className, ...props }) => (
    <strong className={cn('font-semibold', className)} {...props} />
  ),
  pre: ({ className, ...props }) => {
    // oxlint-disable-next-line react/rules-of-hooks -- component identity is stable in memoized markdown components
    const preRef = useRef<HTMLPreElement>(null)
    // oxlint-disable-next-line react/rules-of-hooks
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // oxlint-disable-next-line react/rules-of-hooks
    const [copied, setCopied] = useState(false)

    // oxlint-disable-next-line react/rules-of-hooks
    const handleCopy = useCallback(() => {
      const text = preRef.current?.textContent
      if (text) {
        navigator.clipboard.writeText(text).then(
          () => {
            setCopied(true)
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            timeoutRef.current = setTimeout(() => setCopied(false), 2000)
          },
          () => {
            /* clipboard access denied */
          },
        )
      }
    }, [])

    return (
      <div className={cn('relative group/codeblock my-5', EDGE_TRIM)}>
        <pre
          ref={preRef}
          className={cn(
            'chat-code-block overflow-x-auto rounded-[5px] border border-primary-200 bg-primary-50/80 p-3 text-sm text-primary-900 dark:border-primary-700 dark:bg-primary-900/60 dark:text-primary-100',
            'america:border-blue-200 america:bg-blue-50/70 america:text-blue-950',
            // The america selection colors live in globals.css: the generated
            // `america:selection:*` selectors match every element and carry
            // :has(), so any DOM change restyled the whole document.
            'selection:bg-primary-300/70 selection:text-primary-950 dark:selection:bg-primary-700/70 dark:selection:text-primary-50',
            '[&_code]:!bg-transparent [&_code]:!px-0 [&_code]:!py-0 [&_code]:!rounded-none [&_code]:!text-inherit',
            className,
          )}
          {...props}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              className={cn(
                'absolute top-2 right-2 p-1.5 rounded-md',
                'border border-primary-200/80 bg-white/90 text-primary-600 hover:bg-primary-100 hover:text-primary-900 dark:border-primary-700 dark:bg-primary-900/90 dark:text-primary-300 dark:hover:bg-primary-800 dark:hover:text-primary-100',
                'america:border-blue-200 america:bg-white/95 america:text-blue-700 america:hover:bg-blue-100 america:hover:text-blue-950',
                'opacity-0 group-hover/codeblock:opacity-100 transition-opacity duration-150',
                copied && 'opacity-100',
              )}
              aria-label="Copy code"
            >
              {copied ? <LuCheck size={14} /> : <LuCopy size={14} />}
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" sideOffset={4}>
            {copied ? 'Copied!' : 'Copy code'}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  },
  code: ({ className, ...props }) => {
    return (
      <code
        className={cn(
          'rounded-[5px] bg-primary-100 px-1 py-0.5 font-mono text-[0.95em] text-primary-900 dark:bg-primary-800/70 dark:text-primary-100',
          className,
        )}
        {...props}
      />
    )
  },
  input: ({ className, ...props }) => {
    if (props.type === 'checkbox') {
      return (
        <Checkbox
          className={cn('mb-[2px] mr-[2px]', className)}
          childrenOnly={true}
          {...props}
        />
      )
    }
    return <input {...props} />
  },
})

// Combine memoized standard components with custom citation components
const defaultComponents: CustomComponents = {
  ...memoizedMarkdownComponents,
  'source-citation': SourceCitation,
  'web-search-citation': WebSearchCitation,
  'web-link': WebLink,
  'web-citation': WebCitation,
}

// endregion: Default Markdown Components

// region: Agent Source Citation Cards

// The popover card for each agent, stamped out from the same shared data
// hooks as the pill triggers. Cards mount only while a popover is open, and
// stay memoized so carousel paging doesn't re-render neighbors.
const createAgentSourceCitation = (
  useCitationData: (sourceId: number) => CitationData,
  displayName: string,
) => {
  const AgentSourceCitation = memo(({ sourceId }: SourceCitationProps) => {
    const { source, file, isLoading } = useCitationData(sourceId)

    return (
      <CitationItem
        source={source}
        file={file}
        sourceId={sourceId}
        isLoading={isLoading}
      />
    )
  })
  AgentSourceCitation.displayName = displayName
  return AgentSourceCitation
}

const OpportunityAgentSourceCitation = createAgentSourceCitation(
  useOpportunityCitationData,
  'MarkdownText.OpportunityAgentSourceCitation',
)

const OrganizationAgentSourceCitation = createAgentSourceCitation(
  useOrganizationCitationData,
  'MarkdownText.OrganizationAgentSourceCitation',
)

const PursuitAgentSourceCitation = createAgentSourceCitation(
  usePursuitCitationData,
  'MarkdownText.PursuitAgentSourceCitation',
)

const PursuitDocumentAgentSourceCitation = createAgentSourceCitation(
  usePursuitDocumentCitationData,
  'MarkdownText.PursuitDocumentAgentSourceCitation',
)

// endregion: Agent Source Citation Cards

// endregion: Source Citation

// region: Markdown Implementation

// Memoize the remark plugins array to prevent ReactMarkdown from re-parsing
// on every render when the parent component updates
const remarkPluginsArray = [
  remarkGfm,
  remarkNumberedCitations,
  remarkTableCitations,
  remarkWebCitations,
  remarkWebLinks,
]

/**
 * Custom sanitization schema for chat markdown.
 * Extends the default schema to allow our custom elements while blocking XSS vectors.
 */
const chatSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    // Inline formatting used by LLM-generated highlights, colors, and underlines
    'font',
    'mark',
    'u',
    // Custom citation/link elements used by our remark plugins
    'source-citation',
    'table-cite',
    'web-citation',
    'web-link',
  ],
  attributes: {
    ...(defaultSchema.attributes || {}),
    // Allow all data-* attributes globally (needed for our custom elements)
    '*': [...(defaultSchema.attributes?.['*'] || []), 'data*'],
    // Allow styling attributes for LLM-generated text highlights and font colors
    font: ['color', 'face', 'size', 'style'],
    mark: ['style'],
    span: [...(defaultSchema.attributes?.['span'] || []), 'style'],
    // Explicitly list custom elements (they inherit from '*')
    'source-citation': [],
    'table-cite': [],
    'web-citation': [],
    'web-link': [],
  },
}

const rehypePluginsArray: Parameters<typeof ReactMarkdown>[0]['rehypePlugins'] =
  [rehypeRaw, [rehypeSanitize, chatSanitizeSchema]]

/** Length past which a still-growing list commits its finished items. */
const FORCE_COMMIT_LENGTH = 2000
/** How often to update React state (controls re-render frequency) */
const REACT_UPDATE_INTERVAL_MS = 16 // ~60fps for smooth animation
/**
 * The tail re-renders at most once per this many times its last render's
 * cost. A table or code block is one block until it ends, so it re-parses
 * whole on every render; a long one updates in larger steps instead of
 * taking every frame.
 */
const ACTIVE_RENDER_COST_MULTIPLE = 4
/**
 * Fraction of the reveal backlog caught up per second (exponential easing).
 * Integrated over real elapsed time so reveal speed stays decoupled from the
 * rAF tick rate — slow frames reveal larger increments instead of letting
 * the display drift behind. The display trails the stream by ~1/8s: enough
 * to smooth bursty token arrival, too little to read as delay.
 */
const REVEAL_CATCH_UP_PER_SECOND = 8
/** Floor on reveal speed so small backlogs still finish promptly. */
const REVEAL_MIN_CHARS_PER_SECOND = 120
/**
 * Hard ceiling on how far the typewriter may trail the stream; any excess
 * beyond it reveals immediately, so text that arrives in one jump (a resync
 * after a reconnect or a chat switch) shows at once instead of re-typing.
 */
const REVEAL_MAX_LAG_CHARS = 400

/**
 * Memoized single-block renderer.
 * Committed blocks never re-render regardless of total document length.
 */
export const MemoizedMarkdownBlock = memo(
  ({ content }: { content: string }) => (
    <ReactMarkdown
      remarkPlugins={remarkPluginsArray}
      rehypePlugins={rehypePluginsArray}
      components={defaultComponents}
    >
      {content}
    </ReactMarkdown>
  ),
  (prev, next) => prev.content === next.content,
)

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock'

/**
 * Memoized markdown renderer for active (streaming) content.
 */
interface ActiveMarkdownProps {
  text: string
  /** Adds this render's time, through commit, to the tail's cost. */
  costRef: { current: number }
}

const ActiveMarkdown = memo(
  ({ text, costRef }: ActiveMarkdownProps) => {
    const start = performance.now()
    useLayoutEffect(() => {
      costRef.current += performance.now() - start
    })
    if (!text) return null
    return (
      <ReactMarkdown
        remarkPlugins={remarkPluginsArray}
        rehypePlugins={rehypePluginsArray}
        components={defaultComponents}
      >
        {text}
      </ReactMarkdown>
    )
  },
  (prev, next) => prev.text === next.text,
)

ActiveMarkdown.displayName = 'ActiveMarkdown'

interface CommittedBlocksProps {
  blocks: readonly string[]
}

/**
 * The committed blocks, re-rendered only when one commits. Rendered inline,
 * every reveal frame would walk all of them, and React's per-child bailout
 * checks make that O(answer) per frame.
 */
const CommittedBlocks = memo(({ blocks }: CommittedBlocksProps) => (
  <div className="md-part contents">
    {/* Index as key is safe here: committed blocks are append-only and never reorder */}
    {blocks.map((block, i) => (
      <MemoizedMarkdownBlock key={i} content={block} />
    ))}
  </div>
))

CommittedBlocks.displayName = 'CommittedBlocks'

/** Heal deferral fallback where requestIdleCallback is missing (Safari). */
const IDLE_HEAL_FALLBACK_MS = 200

/**
 * Runs `heal` once the browser is idle so the whole-document completion
 * parse never lands inside a streaming frame. Returns a cancel function.
 */
const scheduleIdleHeal = (heal: () => void): (() => void) => {
  if (typeof requestIdleCallback === 'function') {
    const handle = requestIdleCallback(heal, { timeout: 1000 })
    return () => cancelIdleCallback(handle)
  }
  const handle = setTimeout(heal, IDLE_HEAL_FALLBACK_MS)
  return () => clearTimeout(handle)
}

/**
 * Streaming markdown renderer with typewriter animation and block
 * commitment. Used for main-channel messages that are actively streaming
 * and for the subagent Result box. Per-frame work stays O(active tail):
 * committed blocks live in state and never re-parse.
 */
export const StreamingMarkdownText = memo<
  TextContentPartProps & {
    /** The current text, fresher than the last render's `text`. */
    readText?: () => string
  }
>((props) => {
  const { text, status, readText } = props
  // Only a running stream types. Any settled status — complete, incomplete
  // (cancelled/error), requires-action — must reveal in full: a cancelled
  // stream that keeps typewriting its backlog reads as the message
  // "replaying" after Stop.
  const isStreamComplete = status.type !== 'running'
  const isCompacting = useChatCompactionStatus() === 'summarizing'

  // Animation state in refs
  // Resume at the already-visible text: never re-type content that was
  // already on screen (remounts from hydration, part transitions).
  const visibleTargetRef = useRef<number>(text.length)
  const accumulatorRef = useRef<number>(0)
  const lastRenderTimeRef = useRef<DOMHighResTimeStamp>(0)
  const committedLengthRef = useRef<number>(0)
  const rafRef = useRef<DOMHighResTimeStamp>(0)
  const tailCostRef = useRef<number>(0)
  const lastProcessedTextRef = useRef<string | null>(null)
  const lastProcessedTargetRef = useRef<number>(-1)

  // Keep props in refs for RAF loop access
  const textRef = useRef<string>(text)
  const readTextRef = useRef(readText)
  const isStreamCompleteRef = useRef<boolean>(isStreamComplete)
  textRef.current = text
  readTextRef.current = readText
  isStreamCompleteRef.current = isStreamComplete

  // Array of committed blocks - each renders once and never changes
  const [committedBlocks, setCommittedBlocks] = useState<string[]>([])
  const [activeText, setActiveText] = useState(() =>
    text.length > 0 ? closeUnfinishedSyntax(text) : '',
  )

  // Reset if text got shorter than what we've committed (branch switch,
  // message replacement, stale live-model read). Checked synchronously during
  // render — refs reset immediately, state via effect. Reveal the replacement
  // text immediately instead of re-animating from zero: a shrink is never
  // fresh tokens, and re-typing already-rendered content reads as the thread
  // "re-streaming" after a cancel. A still-running stream resumes its
  // animation from the new end.
  const needsResetRef = useRef<boolean>(false)
  if (text.length < committedLengthRef.current) {
    needsResetRef.current = true
    committedLengthRef.current = 0
    visibleTargetRef.current = text.length
    accumulatorRef.current = 0
    lastRenderTimeRef.current = 0
  }

  // Only run effect when reset is needed (not on every token)
  useEffect(() => {
    if (needsResetRef.current) {
      needsResetRef.current = false
      setCommittedBlocks([])
      setActiveText('')
    }
  })

  // Re-run when isStreamComplete changes (false→true on finish, true→false
  // on regeneration) so the loop restarts for new streaming content.
  // During normal streaming isStreamComplete stays false — effect runs once.
  // After a local cancel, status can remain complete/interrupted while late
  // deltas still grow the text; restart only for that completed-text growth.
  const completedTextLength = isStreamComplete ? text.length : 0
  useEffect(() => {
    // Don't start a duplicate loop if one is already running
    if (rafRef.current) return

    let previousFrameTime: DOMHighResTimeStamp | undefined

    const loop = (now: DOMHighResTimeStamp) => {
      // A read shorter than what committed is a reset, which only a render
      // applies.
      const read = readTextRef.current?.()
      const fullText =
        read !== undefined && read.length >= committedLengthRef.current
          ? read
          : textRef.current
      const isFinished = isStreamCompleteRef.current
      const totalLength = fullText.length
      const backlog = totalLength - visibleTargetRef.current

      const dtSeconds =
        previousFrameTime === undefined
          ? 1 / 60
          : Math.max(0, (now - previousFrameTime) / 1000)
      previousFrameTime = now

      // Caught-up ticks (all text revealed and that state flushed to React,
      // stream still open) skip the slice/close/lex work: with many mounted
      // streams the loop's idle cost is otherwise paid per card per frame.
      // The backlog guard keeps catch-up alive between deltas — a bare
      // at-rest check would advance one step per incoming delta and stall
      // the reveal until the next token.
      if (
        !isFinished &&
        backlog <= 0 &&
        fullText === lastProcessedTextRef.current &&
        visibleTargetRef.current === lastProcessedTargetRef.current
      ) {
        rafRef.current = requestAnimationFrame(loop)
        return
      }

      // Smooth reveal: time-based exponential catch-up with a hard lag cap
      if (isFinished) {
        visibleTargetRef.current = totalLength
      } else if (backlog > 0) {
        const exponentialStep =
          backlog * (1 - Math.exp(-REVEAL_CATCH_UP_PER_SECOND * dtSeconds))
        accumulatorRef.current += Math.max(
          exponentialStep,
          REVEAL_MIN_CHARS_PER_SECOND * dtSeconds,
        )
        const wholeChars = Math.floor(accumulatorRef.current)
        if (wholeChars > 0) {
          accumulatorRef.current -= wholeChars
          visibleTargetRef.current = Math.min(
            visibleTargetRef.current + wholeChars,
            totalLength,
          )
        }
        if (totalLength - visibleTargetRef.current > REVEAL_MAX_LAG_CHARS) {
          visibleTargetRef.current = totalLength - REVEAL_MAX_LAG_CHARS
        }
      }

      // Throttle React renders
      const timeSinceRender = now - lastRenderTimeRef.current
      const isFullyRevealed =
        visibleTargetRef.current >= totalLength && isFinished

      if (
        timeSinceRender >=
          Math.max(
            REACT_UPDATE_INTERVAL_MS,
            ACTIVE_RENDER_COST_MULTIPLE * tailCostRef.current,
          ) ||
        isFullyRevealed
      ) {
        const workStart = performance.now()
        lastRenderTimeRef.current = now

        const committedLen = committedLengthRef.current
        const visibleTarget = visibleTargetRef.current
        const activeTail = fullText.slice(committedLen)
        const localVisible = visibleTarget - committedLen

        // BLOCK COMMIT LOGIC: Commit complete blocks to memoized components
        // This reduces per-frame work from O(total_document) to O(active_tail)
        // Only check for commits if we have enough content and have revealed enough
        if (activeTail.length >= 100 && localVisible > 50) {
          const tokens = marked.lexer(activeTail)
          // Every complete block the reveal has passed commits at once:
          // committing changes nothing on screen, and a block left in the
          // tail is re-parsed every frame, so a cap on commits lets dense
          // markdown (a heading, a list, a table, a code block every few
          // hundred chars) outgrow it and turn each frame O(message).
          let commitLength = 0
          const committing: number[] = []
          for (const token of tokens.slice(0, -1)) {
            if (commitLength + token.raw.length > localVisible) break
            commitLength += token.raw.length
            committing.push(token.raw.length)
          }
          // Something visible stays in the tail (a bare list marker renders
          // nothing): an emptied tail unmounts its wrapper, and removing a
          // child of `.aui-md` restyles every committed block.
          while (
            committing.length > 0 &&
            closeUnfinishedSyntax(
              activeTail.slice(commitLength, localVisible),
            ).trim() === ''
          ) {
            commitLength -= committing.pop()!
          }
          const first = tokens[0]
          if (
            commitLength === 0 &&
            isListToken(first) &&
            first.raw.length > FORCE_COMMIT_LENGTH
          ) {
            // A growing markdown list is ONE lexer token, so a long cited
            // bullet list would otherwise never commit: every citation
            // pill in it stays in the re-parsed active tail — remounting
            // as reveal shifts its position — for the whole stream. Once
            // the list outgrows the budget, commit its fully-revealed
            // items and keep only the still-growing last item active.
            // Chunks render as separate lists until the stream completes
            // and the single-block branch below heals the seams (ordered
            // numbering survives via literal item numbers -> `start`).
            commitLength = revealedListItemsPrefixLength(first, localVisible)
          }

          if (commitLength > 0) {
            // Close any unclosed syntax in the block before committing
            const closedBlock = closeUnfinishedSyntax(
              activeTail.slice(0, commitLength),
            )
            setCommittedBlocks((prev) => [...prev, closedBlock])
            committedLengthRef.current += commitLength
          }
        }

        // Active text is the visible portion after committed blocks
        // closeUnfinishedSyntax ensures unclosed markdown (**, *, `, etc.)
        // is properly closed so it renders as formatted text, not raw markers
        const rawSlice = fullText.slice(
          committedLengthRef.current,
          visibleTarget,
        )
        const newActiveText = closeUnfinishedSyntax(rawSlice)

        setActiveText((prev) => (prev === newActiveText ? prev : newActiveText))

        lastProcessedTextRef.current = fullText
        lastProcessedTargetRef.current = visibleTargetRef.current
        tailCostRef.current = performance.now() - workStart
      }

      if (isFullyRevealed) {
        rafRef.current = 0
        return
      }

      rafRef.current = requestAnimationFrame(loop)
    }

    rafRef.current = requestAnimationFrame(loop)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
  }, [isStreamComplete, completedTextLength])

  const [isHealed, setIsHealed] = useState<boolean>(isStreamComplete)
  useEffect(() => {
    if (!isStreamComplete) {
      setIsHealed(false)
      return
    }
    return scheduleIdleHeal(() => setIsHealed(true))
  }, [isStreamComplete])

  // Completed content renders as one immutable block, byte-identical to the
  // historical branch in MarkdownTextImpl: a single parse that heals any
  // mid-list force-commit seams, after which every citation pill is mounted
  // once and memo-stable. Completion already ends the reveal instantly (the
  // loop jumps visibleTarget to the full length on its next tick), so the
  // heal changes nothing visually — which is why it can wait for an idle
  // moment: parsing the whole accumulated document in the completion frame
  // was a visible hitch, one per finishing stream. Complete-at-mount
  // (historical resume, late-expanded subagent cards) heals immediately.
  if (isStreamComplete && isHealed) {
    return (
      <div className="chat-text-part aui-md w-full" data-status={status.type}>
        <MemoizedMarkdownBlock content={text} />
      </div>
    )
  }

  return (
    // chat-text-part marks the OUTERMOST element of a text part (here and in
    // the historical branch below) — thread-level CSS keys block spacing on
    // it, so both render paths must carry it on their roots.
    <div className="chat-text-part relative w-full">
      <div className="aui-md w-full" data-status={status.type}>
        {/* Committed blocks and the active tail sit in separate `contents`
            wrappers, boxless so layout is unchanged. Chrome restyles a
            parent's whole subtree when a child is removed, or inserted before
            a sibling, under its sibling selectors; the tail does both every
            frame, and as siblings of the committed blocks that restyled the
            whole answer each time. Committed blocks only append. */}
        {committedBlocks.length > 0 && (
          <CommittedBlocks blocks={committedBlocks} />
        )}
        {activeText && (
          <div className="md-part contents">
            <ActiveMarkdown text={activeText} costRef={tailCostRef} />
          </div>
        )}
      </div>
      {/* Sibling of `.aui-md` (not a child) and absolutely positioned, so it
        changes neither the content height nor the markdown's
        `:first-child`/`:last-child` margin stripping — both of which would
        shift the layout when the run completes and the indicator is removed.
        It sits just below the text, where the action controls then appear. */}
      {/* Hidden while compacting: the compaction indicator renders its own
        mark just below, and two pulsing marks would stack. */}
      {status.type === 'running' && !isCompacting && (
        <div className="pointer-events-none absolute left-0 top-full">
          <SweetspotMarkAnimated className="mt-1.5" />
        </div>
      )}
    </div>
  )
})

StreamingMarkdownText.displayName = 'StreamingMarkdownText'

/**
 * Entry point that chooses between streaming and static rendering.
 * - Historical messages (complete on mount): render directly from props
 * - Streaming messages: use block commitment system with typewriter animation
 */
const MarkdownTextImpl: React.FC<TextContentPartProps> = memo((props) => {
  const { status } = props
  // While the part runs, converted text lags the model between structural
  // events — the overlay reads the always-current model text per token.
  const text = useLiveAssistantPartText(status.type === 'running', props.text)
  const readText = useReadLiveAssistantPartText(
    status.type === 'running',
    props.text,
  )
  const isStreamComplete = status.type !== 'running'

  // Track if this message has ever been observed in a live streaming state.
  // If a refresh initially mounts from a stale "complete" render and only later
  // flips to "running", we must stop treating it as a permanently historical
  // message or smooth streaming will stay disabled for the rest of the run.
  const hasSeenStreamingRef = useRef(false)
  if (!isStreamComplete) {
    hasSeenStreamingRef.current = true
  }

  // Tool-call steps arrive as messages whose text part is "" — rendering a
  // wrapper for those leaves an invisible-but-real child that keeps the
  // message shell (padding, action-bar slot) alive and opens phantom gaps in
  // the thread. Nothing to show → render nothing. (While running, keep the
  // wrapper: it hosts the streaming indicator and the text may still arrive.)
  if (text.length === 0 && status.type !== 'running') {
    return null
  }

  const shouldShowImmediately =
    isStreamComplete && text.length > 0 && !hasSeenStreamingRef.current

  // Historical messages: render directly from props (always synced, no stale state)
  if (shouldShowImmediately) {
    return (
      <div className="chat-text-part aui-md w-full" data-status={status.type}>
        <MemoizedMarkdownBlock content={text} />
      </div>
    )
  }

  // Streaming messages: use the block commitment system
  return <StreamingMarkdownText {...props} text={text} readText={readText} />
})

MarkdownTextImpl.displayName = 'MarkdownTextImpl'

export const MarkdownText = MarkdownTextImpl

// endregion: MarkdownText
